import { HttpErrorResponse } from '@angular/common/http';
import { ResourceRef } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withProps } from '@ngrx/signals';
import { setAllEntities } from '@ngrx/signals/entities';
import { Bmi, BmiApi } from '@types-lib';
import { isEqual, uniqBy } from 'lodash';
import { throwError } from 'rxjs';
import { BmiStoreProps } from './bmi-store.model';
import { bmiItemConfig } from './bmi-store.store';

export function withBmiStoreResources<_>() {
  return signalStoreFeature(
    {
      props: type<BmiStoreProps>(),
      state: type<Bmi.BmiStoreState>(),
    },
    withProps(store => {
      const _loadBmiDataResource: ResourceRef<BmiApi.UserBiometricsResponse | undefined> = rxResource({
        params: (): Bmi.LoadBmiDataReq => ({
          userId: store.patientUserId(),
          limit: store.bmiListOptions.pagination.limit(),
          order: store.bmiListOptions.sort(),
          serviceCodesFilter: store.bmiListOptions.filterBy.serviceCodes(),
          offset: store.bmiListOptions.pagination.offset(),
        }),
        stream: ({ params }) =>
          store._getUserBodyMassIndexInfoEndpoint.get(params).pipe(
            tapResponse({
              next: ({ pagination, data: { treatment_details, list } }) => {
                const deduplicatedList = uniqBy(list, item => JSON.stringify(item));
                const deduplicatedStoreList = uniqBy(Object.values(store.bmiItemEntities()), item =>
                  JSON.stringify(item),
                );

                const treatmentChanged = !isEqual(treatment_details, store.treatmentDetails());
                const listChanged = !isEqual(deduplicatedList, deduplicatedStoreList);

                if (treatmentChanged || listChanged) {
                  patchState(store, {
                    treatmentDetails: treatment_details,
                    bmiListOptions: {
                      ...store.bmiListOptions(),
                      pagination,
                    },
                    itemBeingEditedId: null,
                  });
                  patchState(store, setAllEntities(deduplicatedList, bmiItemConfig));
                }
              },
              error: (err: HttpErrorResponse) => {
                console.error(err);
              },
            }),
          ),
        equal: (previous, current) => {
          if (!previous?.data || !current?.data) return previous === current;
          const prevList = uniqBy(previous.data.list, item => JSON.stringify(item));
          const currList = uniqBy(current.data.list, item => JSON.stringify(item));
          return (
            isEqual(previous.data.treatment_details, current.data.treatment_details) && isEqual(prevList, currList)
          );
        },
      });

      const _updateTreatmentStartResource: ResourceRef<BmiApi.SavedBmiItem | undefined> = rxResource({
        params: () => store._updateTreatmentStartInfoTrigger(),
        stream: ({ params }) =>
          params
            ? store._setTreatmentStartDataEndpoint.put(params).pipe(
                tapResponse({
                  next: () => {
                    patchState(store, {
                      treatmentDetails: {
                        ...store.treatmentDetails(),
                        start_date: params?.start_date as string,
                        start_weight: params?.start_weight,
                      },
                    });
                    _loadBmiDataResource.reload();
                  },
                  error: (err: HttpErrorResponse) => {
                    console.error(err);
                  },
                }),
              )
            : throwError(() => 'Empty PUT bmi treatment start request in _updateTreatmentStartResource'),
      });

      const _updateBmiItemResource: ResourceRef<BmiApi.SavedBmiItem | undefined> = rxResource({
        params: () => store._updateBmiItemTrigger(),
        stream: ({ params }) =>
          params
            ? store._setOrderBodyMassIndexInfoEndpoint.put(params).pipe(
                tapResponse({
                  next: () => {
                    _loadBmiDataResource.reload();
                  },
                  error: (err: HttpErrorResponse) => {
                    console.error(err);
                  },
                }),
              )
            : throwError(() => 'Empty PUT bmi treatment start request in _updateTreatmentStartResource'),
      });

      return {
        _loadBmiDataResource,
        _updateTreatmentStartResource,
        _updateBmiItemResource,
      };
    }),
    withProps(store => ({
      loadBmiDataResource: store._loadBmiDataResource,
      updateTreatmentStartResource: store._updateTreatmentStartResource.asReadonly(),
      updateBmiItemResource: store._updateBmiItemResource.asReadonly(),
    })),
  );
}
